import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FormDialog } from "@/components/common/Dialog";
import { Input } from "@/components/common/Input";
import { Label } from "@/components/common/FormLabel";
import { ErrorLabel } from "@/components/common/ErrorLabel";
import { Spinner } from "@/components/common/Loading";
import { AddressField, EmailField, AnalysisItemField, UserDefinedItemField } from "@/components/common/FormFields";
import { fetchQuickCreateProspectOptions, quickCreateProspect } from "@/api/prospect/api";
import { searchProspectUdLookup } from "@/api/autocomplete/api";
import { toSelectOptions } from "@/utils/fieldValidation";
import { searchAnalysisValues } from "@/api/autocomplete/api";
import { SelectOption } from "@/components/common/Dropdown";
import { QuickCreateProspectOptions, AnalysisValueSubmit } from "@/types/domain";
import { showErrorToast, showSuccessToast } from "@/hooks/use-toast";
import { collectUserDefinedValues } from "@/utils/reactHookForm";

type FormValues = {
  companyName: string;
  accountNo: string;
  telephone: string;
  fax: string;
  email: string;
  webAddress: string;
  comments: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  town: string;
  county: string;
  country: string;
  postcode: string;
  [key: string]: string | boolean | SelectOption | null; // analysis_<index> stores SelectOption|null for autocomplete items
};

type QuickProspectModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProspectCreated: (companyId: string, companyName: string) => void;
};

export const QuickProspectModal = ({ open, onOpenChange, onProspectCreated }: QuickProspectModalProps) => {
  const [options, setOptions] = useState<QuickCreateProspectOptions | null>(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [autoGenerateAccountNo, setAutoGenerateAccountNo] = useState(true);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      companyName: "", accountNo: "", telephone: "", fax: "", email: "",
      webAddress: "", comments: "", address1: "", address2: "", address3: "",
      address4: "", town: "", county: "", country: "", postcode: "",
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    let isActive = true;
    setIsLoadingOptions(true);

    fetchQuickCreateProspectOptions()
      .then((data) => {
        if (!isActive) {
          return;
        }

        setOptions(data);
        setAutoGenerateAccountNo((prev) => {
          if (data.accountNoAutoGenerate === "Mandatory") {
            return true;
          }
          return prev;
        });

        data.analysisItems?.forEach((item) => {
          const fieldKey = `analysis_${item.index}`;
          const currentValue = getValues(fieldKey);
          if (currentValue != null && currentValue !== "") {
            return;
          }

          if (item.defaultCode && item.isAutoComplete) {
            setValue(fieldKey, { value: item.defaultCode, label: item.defaultDescription ?? item.defaultCode });
          }

          if (item.defaultCode && !item.isAutoComplete) {
            setValue(fieldKey, item.defaultCode);
          }
        });
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        showErrorToast("Quick Prospect could not load.");
        onOpenChange(false);
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingOptions(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [onOpenChange, open]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      reset();
      setServerError(null);
      setIsLoadingOptions(false);
    }
    onOpenChange(isOpen);
  };

  const loadAnalysisOptions = (index: number) => async (inputValue: string) => {
    const results = await searchAnalysisValues(index, inputValue, "Company").catch(() => []);
    return results.map((r) => ({ value: r.code, label: r.description }));
  };

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    setIsSaving(true);
    try {
      const analysisValues: AnalysisValueSubmit[] = [];
      options?.analysisItems?.forEach((item) => {
        const fieldKey = `analysis_${item.index}`;
        if (item.isAutoComplete) {
          const sel = values[fieldKey] as SelectOption | null | undefined;
          if (sel && typeof sel === "object") {
            analysisValues.push({ index: item.index, code: sel.value, description: sel.label });
          }
        } else {
          const code = (values[fieldKey] as string) ?? "";
          if (code) {
            const label = item.values?.find((v) => v.code === code)?.description ?? "";
            analysisValues.push({ index: item.index, code, description: label });
          }
        }
      });

      const userDefinedValues = collectUserDefinedValues(options?.userDefinedGroups, values);

      const result = await quickCreateProspect({
        companyName: values.companyName,
        accountNo: (!autoGenerateAccountNo && values.accountNo) ? values.accountNo : undefined,
        telephone: values.telephone || undefined,
        fax: values.fax || undefined,
        email: values.email || undefined,
        webAddress: values.webAddress || undefined,
        comments: values.comments || undefined,
        address1: values.address1 || undefined,
        address2: values.address2 || undefined,
        address3: values.address3 || undefined,
        address4: values.address4 || undefined,
        town: values.town || undefined,
        county: values.county || undefined,
        country: values.country || undefined,
        postcode: values.postcode || undefined,
        analysisValues: analysisValues.length > 0 ? analysisValues : undefined,
        userDefinedValues: userDefinedValues.length > 0 ? userDefinedValues : undefined,
      });

      if (result.success && result.companyId && result.companyName) {
        showSuccessToast("Prospect added successfully.");
        onProspectCreated(result.companyId, result.companyName);
        handleOpenChange(false);
      } else {
        setServerError(result.errorMessage ?? "Failed to create prospect.");
      }
    } catch {
      setServerError("An unexpected error occurred.");
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Quick Prospect"
      onSubmit={onSubmit}
      submitText="Create Prospect"
      loading={isSaving}
      disableSubmit={isLoadingOptions}
      hideTrigger={true}
      disableCloseOnOutsideClick={true}
    >
      <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto pr-1">
        {isLoadingOptions ? (
          <div className="flex justify-center py-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
        {serverError && (
          <p className="text-sm text-red-600">{serverError}</p>
        )}

        {/* Account No — always shown */}
        <div>
          <Label forField="ap-account-no" label={<>Account No <span className="text-red-500">*</span></>} />
          {options?.accountNoAutoGenerate !== "Disabled" && (
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="ap-account-no-auto"
                className="h-4 w-4"
                checked={autoGenerateAccountNo}
                disabled={options?.accountNoAutoGenerate === "Mandatory"}
                onChange={(e) => setAutoGenerateAccountNo(e.target.checked)}
              />
              <label htmlFor="ap-account-no-auto" className="text-sm text-gray-600">Auto-generate</label>
            </div>
          )}
          {(options?.accountNoAutoGenerate === "Disabled" || !autoGenerateAccountNo) && (
            <>
              <Input
                id="ap-account-no"
                type="text"
                maxLength={50}
                inErrorState={Boolean(errors.accountNo)}
                {...register("accountNo", {
                  required: "Account No is required",
                  maxLength: { value: 50, message: "Account No must be 50 characters or less" },
                })}
              />
              <ErrorLabel error={errors.accountNo?.message} />
            </>
          )}
        </div>

        {/* Company Name — always shown */}
        <div>
          <Label forField="ap-company-name" label={<>Company Name <span className="text-red-500">*</span></>} />
          <Input
            id="ap-company-name"
            type="text"
            maxLength={50}
            inErrorState={Boolean(errors.companyName)}
            {...register("companyName", {
              required: "Company name is required",
              maxLength: { value: 50, message: "Company name must be 50 characters or less" },
            })}
          />
          <ErrorLabel error={errors.companyName?.message} />
        </div>

        {/* Telephone — shown when mandatory */}
        {options?.showTelephone && (
          <div>
            <Label forField="ap-telephone" label={<>Telephone <span className="text-red-500">*</span></>} />
            <Input
              id="ap-telephone"
              type="text"
              maxLength={50}
              inErrorState={Boolean(errors.telephone)}
              {...register("telephone", {
                required: "Telephone is required",
                maxLength: { value: 50, message: "Telephone must be 50 characters or less" },
              })}
            />
            <ErrorLabel error={errors.telephone?.message} />
          </div>
        )}

        {/* Fax */}
        {options?.showFax && (
          <div>
            <Label forField="ap-fax" label={<>Fax <span className="text-red-500">*</span></>} />
            <Input
              id="ap-fax"
              type="text"
              maxLength={50}
              inErrorState={Boolean(errors.fax)}
              {...register("fax", {
                required: "Fax is required",
                maxLength: { value: 50, message: "Fax must be 50 characters or less" },
              })}
            />
            <ErrorLabel error={errors.fax?.message} />
          </div>
        )}

        {/* Email */}
        {options?.showEmail && (
          <EmailField
            fieldKey="email"
            id="ap-email"
            register={register}
            errors={errors}
          />
        )}

        {/* Web Address */}
        {options?.showWebAddress && (
          <div>
            <Label forField="ap-web" label={<>Web Address <span className="text-red-500">*</span></>} />
            <Input
              id="ap-web"
              type="text"
              maxLength={150}
              inErrorState={Boolean(errors.webAddress)}
              {...register("webAddress", {
                required: "Web Address is required",
                maxLength: { value: 150, message: "Web Address must be 150 characters or less" },
              })}
            />
            <ErrorLabel error={errors.webAddress?.message} />
          </div>
        )}

        {/* Address block */}
        {options?.showAddress && (
          <AddressField
            idPrefix="ap-"
            register={register}
            errors={errors}
          />
        )}

        {/* Comments */}
        {options?.showComments && (
          <div>
            <Label forField="ap-comments" label={<>Comments <span className="text-red-500">*</span></>} />
            <Input
              id="ap-comments"
              multiline
              rows={3}
              maxLength={8000}
              inErrorState={Boolean(errors.comments)}
              {...register("comments", { required: "Comments are required", maxLength: { value: 8000, message: "Comments must be 8000 characters or less" } })}
            />
            <ErrorLabel error={errors.comments?.message} />
          </div>
        )}

        {/* Mandatory analysis items */}
        {options?.analysisItems && options.analysisItems.length > 0 && (
          <hr className="border-gray-200 my-6" />
        )}
        {options?.analysisItems?.map((item) => (
          <AnalysisItemField
            key={item.index}
            item={item}
            fieldKey={`analysis_${item.index}`}
            control={control}
            register={register}
            errors={errors}
            loadOptions={loadAnalysisOptions(item.index)}
          />
        ))}

        {/* Mandatory user-defined groups */}
        {options?.userDefinedGroups && options.userDefinedGroups.length > 0 && (
          <hr className="border-gray-200 my-6" />
        )}
        {options?.userDefinedGroups?.map((group) => (
          <div key={group.id} className="rounded-md border border-gray-200 p-3">
            {group.name && (
              <h3 className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-3">{group.name}</h3>
            )}
            <div className="space-y-3">
              {group.items.map((item) => (
                <UserDefinedItemField
                  key={item.id}
                  item={item}
                  fieldKey={`ud_${item.itemId}`}
                  register={register}
                  control={control}
                  errors={errors}
                  loadOptions={item.isAutoComplete ? (q) => searchProspectUdLookup(item.itemId, q).then(toSelectOptions) : undefined}
                />
              ))}
            </div>
          </div>
        ))}
          </>
        )}
      </div>
    </FormDialog>
  );
};

